import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  App,
  Button,
  Checkbox,
  InputNumber,
  Radio,
  Select,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd'
import { PlusOutlined, TableOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, PAGE_SIZE } from '../api'
import { OrgPersonBrowseField, OrgUnitBrowseField, RoleBrowseField, tablePagination } from './ui'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import {
  clampSharePermission,
  permissionOptionsForCap,
  type ShareGrantCap,
} from '../lib/shareGrantCap'
import './KnowledgeFolderShareRules.css'

type GrantRole = 'sharer' | 'participant'

type ShareRuleRecord = {
  key: string
  subject_type: string
  subject_type_label: string
  subject_id?: number | null
  subject_name: string
  category: 'creator' | 'general'
  permission: 'view' | 'edit' | 'full'
  permission_label: string
  downloadable: boolean
  downloadable_label: string
  sec_level_min: number
  sec_level_max: number
  sec_level_label: string
  include_subordinates?: boolean
  include_part_time?: boolean
}

type Props = {
  folderId: number | null
  scope: KnowledgeScope
  dimensionId?: number | null
  disabled?: boolean
}

const DOC_PERM_TABS: { key: GrantRole | string; label: string; implemented: boolean }[] = [
  { key: 'sharer', label: '默认共享人', implemented: true },
  { key: 'participant', label: '默认参与人', implemented: true },
  { key: 'create', label: '新建权限', implemented: false },
  { key: 'download', label: '下载权限', implemented: false },
  { key: 'delete', label: '删除权限', implemented: false },
  { key: 'copy', label: '复制权限', implemented: false },
  { key: 'move', label: '移动权限', implemented: false },
]

const CREATOR_TYPES = [
  { value: 'creator_self', label: '创建人本人' },
  { value: 'creator_direct_supervisor', label: '创建人直接上级' },
  { value: 'creator_all_supervisors', label: '创建人所有上级' },
  { value: 'creator_same_department', label: '本部门' },
  { value: 'creator_same_position', label: '本岗位' },
  { value: 'creator_same_branch', label: '本分部' },
]

const GENERAL_TYPES = [
  { value: 'user', label: '人员' },
  { value: 'org_unit', label: '部门' },
  { value: 'branch', label: '分部' },
  { value: 'role', label: '角色' },
  { value: 'group', label: '群组' },
  { value: 'position', label: '岗位' },
  { value: 'customer', label: '客户' },
  { value: 'customer_type', label: '客户类型' },
  { value: 'everyone', label: '所有人' },
  { value: 'external_contact', label: '外部联系人' },
  { value: 'all_external', label: '所有外部人' },
]

const BACKEND_SUPPORTED_TYPES = new Set([
  ...CREATOR_TYPES.map((x) => x.value),
  'user',
  'org_unit',
  'role',
])

const PICKER_TYPES = new Set(['user', 'org_unit', 'role'])

function labelOfSubject(type: string) {
  return (
    [...CREATOR_TYPES, ...GENERAL_TYPES].find((x) => x.value === type)?.label ||
    type
  )
}

function defaultCreatorRule(): ShareRuleRecord {
  return {
    key: 'default-creator-self',
    subject_type: 'creator_self',
    subject_type_label: '创建人本人',
    subject_name: '创建人本人',
    category: 'creator',
    permission: 'full',
    permission_label: '完全控制',
    downloadable: false,
    downloadable_label: '--',
    sec_level_min: 0,
    sec_level_max: 100,
    sec_level_label: '--',
  }
}

function apiRowToRecord(row: Record<string, unknown>, idx: number): ShareRuleRecord {
  const subjectType = String(row.subject_type ?? '')
  return {
    key: String(row.id ?? `r-${idx}`),
    subject_type: subjectType,
    subject_type_label: labelOfSubject(subjectType),
    subject_id: (row.subject_id as number | null | undefined) ?? null,
    subject_name: String(row.subject_name ?? labelOfSubject(subjectType)),
    category: (row.category as 'creator' | 'general') || 'creator',
    permission: (row.permission as ShareRuleRecord['permission']) || 'view',
    permission_label: String(row.permission_label ?? '查看'),
    downloadable: !!row.downloadable,
    downloadable_label: String(row.downloadable_label ?? (row.downloadable ? '可下载' : '--')),
    sec_level_min: Number(row.sec_level_min ?? 0),
    sec_level_max: Number(row.sec_level_max ?? 100),
    sec_level_label: String(row.sec_level_label ?? '--'),
    include_subordinates: !!row.include_subordinates,
    include_part_time: !!row.include_part_time,
  }
}

export function KnowledgeFolderDocPermissions({ folderId, scope, dimensionId, disabled }: Props) {
  const { modal } = App.useApp()
  const [permTab, setPermTab] = useState<string>('sharer')
  const [categoryTab, setCategoryTab] = useState<'creator' | 'general'>('general')
  const [rulesByRole, setRulesByRole] = useState<Record<GrantRole, ShareRuleRecord[]>>({
    sharer: [],
    participant: [],
  })
  const [loading, setLoading] = useState(false)
  const [listPage, setListPage] = useState(1)

  const [subjectType, setSubjectType] = useState('org_unit')
  const [subjectId, setSubjectId] = useState<number | null>(null)
  const [subjectName, setSubjectName] = useState('')
  const [includeSubordinates, setIncludeSubordinates] = useState(false)
  const [includePartTime, setIncludePartTime] = useState(false)
  const [secMin, setSecMin] = useState(0)
  const [secMax, setSecMax] = useState(100)
  const [permission, setPermission] = useState<'view' | 'edit' | 'full'>('view')
  const [downloadable, setDownloadable] = useState(true)
  const [grantCap, setGrantCap] = useState<ShareGrantCap | null>(null)

  const permissionOptions = useMemo(() => permissionOptionsForCap(grantCap), [grantCap])

  const scopeParams = useMemo(
    () => ({
      scope,
      ...(dimensionId != null ? { dimension_id: dimensionId } : {}),
    }),
    [scope, dimensionId],
  )

  const activeTabMeta = DOC_PERM_TABS.find((t) => t.key === permTab)
  const activeGrantRole = (permTab === 'participant' ? 'participant' : 'sharer') as GrantRole
  const currentList = rulesByRole[activeGrantRole] ?? []
  const pagedList = currentList.slice((listPage - 1) * PAGE_SIZE, listPage * PAGE_SIZE)
  const formEnabled = !!activeTabMeta?.implemented

  const resetForm = useCallback((category: 'creator' | 'general') => {
    if (category === 'creator') {
      setSubjectType('creator_self')
      setSubjectId(null)
      setSubjectName('创建人本人')
    } else {
      setSubjectType('org_unit')
      setSubjectId(null)
      setSubjectName('')
    }
    setIncludeSubordinates(false)
    setIncludePartTime(false)
    setSecMin(0)
    setSecMax(100)
    const clamped = clampSharePermission('view', true, grantCap)
    setPermission(clamped.permission)
    setDownloadable(clamped.downloadable)
  }, [grantCap])

  useEffect(() => {
    if (!folderId || scope !== 'enterprise') return
    adminApi
      .get<ShareGrantCap>(`/admin/knowledge/folders/${folderId}/share-grant-cap`, { params: scopeParams })
      .then((r) => setGrantCap(r.data))
      .catch(() => setGrantCap(null))
  }, [folderId, scope, scopeParams])

  useEffect(() => {
    if (!folderId || scope !== 'enterprise') return
    setLoading(true)
    adminApi
      .get<{ default_sharers: Record<string, unknown>[]; default_participants: Record<string, unknown>[] }>(
        `/admin/knowledge/folders/${folderId}/share-rules`,
        { params: scopeParams },
      )
      .then((r) => {
        const sharers = (r.data.default_sharers || []).map(apiRowToRecord)
        const participants = (r.data.default_participants || []).map(apiRowToRecord)
        setRulesByRole({
          sharer: sharers.length ? sharers : [defaultCreatorRule()],
          participant: participants,
        })
        setListPage(1)
        resetForm('general')
        setCategoryTab('general')
        setPermTab('sharer')
      })
      .catch((e) => message.error(formatApiError(e, '加载文档权限失败')))
      .finally(() => setLoading(false))
  }, [folderId, scope, scopeParams, resetForm])

  useEffect(() => {
    if (categoryTab === 'creator') {
      const label = labelOfSubject(subjectType)
      setSubjectName(label)
      setSubjectId(null)
    }
  }, [categoryTab, subjectType])

  const onCategoryChange = (category: 'creator' | 'general') => {
    setCategoryTab(category)
    resetForm(category)
  }

  const onSubjectTypeChange = (type: string) => {
    setSubjectType(type)
    if (categoryTab === 'creator') {
      setSubjectName(labelOfSubject(type))
      setSubjectId(null)
    } else if (PICKER_TYPES.has(type)) {
      setSubjectId(null)
      setSubjectName('')
    } else {
      setSubjectId(null)
      setSubjectName(labelOfSubject(type))
    }
  }

  const addRule = () => {
    if (!formEnabled) return
    if (!BACKEND_SUPPORTED_TYPES.has(subjectType)) {
      message.info('该对象类型暂未支持')
      return
    }
    if (PICKER_TYPES.has(subjectType) && subjectId == null) {
      message.warning('请选择对象')
      return
    }
    if (secMin > secMax) {
      message.warning('安全级别范围不正确')
      return
    }
    const typeLabel = labelOfSubject(subjectType)
    const clamped = clampSharePermission(permission, downloadable, grantCap)
    if (clamped.permission !== permission || clamped.downloadable !== downloadable) {
      message.warning('权限不可高于您自身的目录权限')
      setPermission(clamped.permission)
      setDownloadable(clamped.downloadable)
      return
    }
    const permLabel = permissionOptions.find((p) => p.value === permission)?.label ?? permission
    const row: ShareRuleRecord = {
      key: `local-${Date.now()}`,
      subject_type: subjectType,
      subject_type_label: typeLabel,
      subject_id: subjectId,
      subject_name: PICKER_TYPES.has(subjectType) ? subjectName : typeLabel,
      category: categoryTab,
      permission: clamped.permission,
      permission_label: permLabel,
      downloadable: clamped.downloadable,
      downloadable_label: downloadable ? '可下载' : '--',
      sec_level_min: secMin,
      sec_level_max: secMax,
      sec_level_label: secMin === 0 && secMax === 100 ? '--' : `${secMin}-${secMax}`,
      include_subordinates: includeSubordinates,
      include_part_time: includePartTime,
    }
    const dup = currentList.some(
      (r) => r.subject_type === row.subject_type && (r.subject_id ?? null) === (row.subject_id ?? null),
    )
    if (dup) {
      message.warning('共享列表中已存在相同对象')
      return
    }
    const nextRules = {
      ...rulesByRole,
      [activeGrantRole]: [...rulesByRole[activeGrantRole], row],
    }
    setRulesByRole(nextRules)
    void persistRules(nextRules)
      .then(() => message.success('文档权限已保存'))
      .catch(() => {})
  }

  const removeRule = (key: string) => {
    if (key === 'default-creator-self') {
      message.info('默认创建人规则不可删除')
      return
    }
    modal.confirm({
      title: '删除该共享规则？',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        const nextRules = {
          ...rulesByRole,
          [activeGrantRole]: rulesByRole[activeGrantRole].filter((r) => r.key !== key),
        }
        setRulesByRole(nextRules)
        void persistRules(nextRules)
          .then(() => message.success('文档权限已保存'))
          .catch(() => {})
      },
    })
  }

  const persistRules = (rules: Record<GrantRole, ShareRuleRecord[]>) => {
    if (!folderId) return Promise.reject(new Error('未选择目录'))
    const payload = (rows: ShareRuleRecord[]) =>
      rows
        .filter((r) => r.key !== 'default-creator-self')
        .map((r) => ({
          subject_type: r.subject_type,
          subject_id: r.subject_id ?? null,
          category: r.category,
          permission: r.permission,
          downloadable: r.downloadable,
          sec_level_min: r.sec_level_min,
          sec_level_max: r.sec_level_max,
          include_subordinates: r.include_subordinates,
          include_part_time: r.include_part_time,
        }))
    return adminApi
      .put(
        `/admin/knowledge/folders/${folderId}/share-rules`,
        {
          default_sharers: payload(rules.sharer),
          default_participants: payload(rules.participant),
        },
        { params: scopeParams },
      )
      .catch((e) => {
        message.error(formatApiError(e, '保存文档权限失败'))
        throw e
      })
  }

  const save = () => persistRules(rulesByRole)

  const showOrgOptions = categoryTab === 'general' && subjectType === 'org_unit'
  const showStaticObject = categoryTab === 'creator' || (categoryTab === 'general' && !PICKER_TYPES.has(subjectType))

  if (scope !== 'enterprise') {
    return (
      <Typography.Text type="secondary">文档权限仅在企业知识库目录中可用。</Typography.Text>
    )
  }

  const formPanel = formEnabled ? (
    <div className="kf-doc-perm-form-box">
      <div className="kf-doc-perm-category-tabs">
        <button
          type="button"
          className={`kf-doc-perm-category-tab${categoryTab === 'creator' ? ' is-active' : ''}`}
          onClick={() => onCategoryChange('creator')}
          disabled={disabled || loading}
        >
          与创建人相关
        </button>
        <button
          type="button"
          className={`kf-doc-perm-category-tab${categoryTab === 'general' ? ' is-active' : ''}`}
          onClick={() => onCategoryChange('general')}
          disabled={disabled || loading}
        >
          通用
        </button>
      </div>

      <div className="kf-doc-perm-form-grid">
        <div className="kf-doc-perm-form-label">对象类型</div>
        <Radio.Group
          className="kf-doc-perm-radio-row"
          value={subjectType}
          onChange={(e) => onSubjectTypeChange(e.target.value)}
          disabled={disabled || loading}
        >
          {(categoryTab === 'creator' ? CREATOR_TYPES : GENERAL_TYPES).map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              {opt.label}
            </Radio>
          ))}
        </Radio.Group>

        <div className="kf-doc-perm-form-label">对象</div>
        <div className="kf-doc-perm-object-field">
          {showStaticObject ? (
            <div className="kf-doc-perm-object-display is-static">
              <span>{subjectName || labelOfSubject(subjectType)}</span>
            </div>
          ) : subjectType === 'user' ? (
            <OrgPersonBrowseField
              disabled={disabled || loading}
              value={
                subjectId != null
                  ? { userId: subjectId, personId: 0, name: subjectName || `用户#${subjectId}` }
                  : null
              }
              onChange={(next) => {
                setSubjectId(next?.userId ?? null)
                setSubjectName(next?.name ?? '')
              }}
            />
          ) : subjectType === 'org_unit' ? (
            <OrgUnitBrowseField
              disabled={disabled || loading}
              value={subjectId != null ? { id: subjectId, name: subjectName } : null}
              onChange={(next) => {
                setSubjectId(next?.id ?? null)
                setSubjectName(next?.name ?? '')
              }}
            />
          ) : subjectType === 'role' ? (
            <RoleBrowseField
              disabled={disabled || loading}
              value={subjectId != null ? { id: subjectId, name: subjectName } : null}
              onChange={(next) => {
                setSubjectId(next?.id ?? null)
                setSubjectName(next?.name ?? '')
              }}
            />
          ) : (
            <div className="kf-doc-perm-object-display is-static">
              <span>{labelOfSubject(subjectType)}</span>
              <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                暂未支持浏览选择
              </Typography.Text>
            </div>
          )}
        </div>

        {showOrgOptions ? (
          <>
            <div className="kf-doc-perm-form-label" />
            <div className="kf-doc-perm-check-row">
              <Checkbox
                checked={includeSubordinates}
                onChange={(e) => setIncludeSubordinates(e.target.checked)}
                disabled={disabled || loading}
              >
                包含下级
              </Checkbox>
              <Checkbox
                checked={includePartTime}
                onChange={(e) => setIncludePartTime(e.target.checked)}
                disabled={disabled || loading}
              >
                包含兼职
              </Checkbox>
            </div>
          </>
        ) : null}

        <div className="kf-doc-perm-form-label">安全级别</div>
        <div className="kf-doc-perm-sec-range">
          <InputNumber min={0} max={100} value={secMin} onChange={(v) => setSecMin(Number(v ?? 0))} disabled={disabled || loading} />
          <span>-</span>
          <InputNumber min={0} max={100} value={secMax} onChange={(v) => setSecMax(Number(v ?? 100))} disabled={disabled || loading} />
        </div>

        <div className="kf-doc-perm-form-label">权限</div>
        <div className="kf-doc-perm-perm-row">
          <Select
            value={permission}
            options={permissionOptions}
            onChange={(v) => {
              const next = clampSharePermission(v, downloadable, grantCap)
              setPermission(next.permission)
              setDownloadable(next.downloadable)
            }}
            disabled={disabled || loading}
          />
          <Checkbox
            checked={downloadable}
            onChange={(e) => setDownloadable(e.target.checked)}
            disabled={disabled || loading || permission !== 'view' || !grantCap?.allow_downloadable}
          >
            可下载
          </Checkbox>
        </div>
      </div>

      <div className="kf-doc-perm-form-actions">
        <Button type="primary" onClick={addRule} disabled={disabled || loading}>
          确定
        </Button>
      </div>
    </div>
  ) : (
    <div className="kf-doc-perm-empty-hint">该权限类型暂未开放，后续版本支持。</div>
  )

  return (
    <div className="kf-doc-perm-shell">
      <Tabs
        className="kf-doc-perm-subtabs"
        type="card"
        activeKey={permTab}
        destroyOnHidden={false}
        onChange={(k) => {
          setPermTab(k)
          setListPage(1)
        }}
        items={DOC_PERM_TABS.map((t) => ({ key: t.key, label: t.label }))}
      />

      <div className="kf-doc-perm-body">
        {formPanel}

        {formEnabled ? (
          <>
            <div className="kf-doc-perm-list-head">
              <span className="kf-doc-perm-list-title">共享列表</span>
              <span className="kf-doc-perm-list-tools">
                <Button type="text" icon={<PlusOutlined />} aria-label="新增" disabled={disabled || loading} />
                <Button type="text" icon={<TableOutlined />} aria-label="列表" disabled />
              </span>
            </div>

            <Table<ShareRuleRecord>
              className="kf-doc-perm-table"
              rowKey="key"
              size="small"
              loading={loading}
              dataSource={pagedList}
              pagination={tablePagination(listPage, currentList.length, (p) => setListPage(p), {
                pageSize: PAGE_SIZE,
              })}
              locale={{ emptyText: '暂无共享规则' }}
              onRow={(row) => ({
                onContextMenu: (e) => {
                  e.preventDefault()
                  if (row.key !== 'default-creator-self') removeRule(row.key)
                },
              })}
              columns={[
                { title: '对象类型', dataIndex: 'subject_type_label', width: 120 },
                { title: '对象', dataIndex: 'subject_name', ellipsis: true },
                { title: '安全级别', dataIndex: 'sec_level_label', width: 96 },
                { title: '权限', dataIndex: 'permission_label', width: 96 },
                { title: '下载权限', dataIndex: 'downloadable_label', width: 96 },
              ]}
            />
          </>
        ) : null}
      </div>

      <ShareRulesSaveBridge onSave={save} />
    </div>
  )
}

const saveHandlers: Array<() => Promise<unknown>> = []

export function registerShareRulesSave(handler: () => Promise<unknown>) {
  saveHandlers.length = 0
  saveHandlers.push(handler)
}

export async function flushShareRulesSave() {
  if (!saveHandlers.length) return
  await saveHandlers[0]()
}

function ShareRulesSaveBridge({ onSave }: { onSave: () => Promise<unknown> }) {
  useEffect(() => {
    registerShareRulesSave(onSave)
    return () => {
      saveHandlers.length = 0
    }
  }, [onSave])
  return null
}

/** @deprecated 使用 KnowledgeFolderDocPermissions */
export const KnowledgeFolderShareRules = KnowledgeFolderDocPermissions
